import { cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { Fragment, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDate } from "@/lib/utils/date";
import {
  DocumentTable,
  type DocumentTableProps,
} from "../components/document-table/document-table";
import type { DocumentData } from "../components/document-table/types";
import { useDocumentActions } from "../components/document-table/use-document-actions";
import {
  BuildingNav,
  interventionLots,
  ZoneCard,
} from "../components/study-progress";

type DoZoneSummary = {
  id: number;
  name: string;
  shortName: string | null;
  type: "horizontale" | "verticale";
  interventionCount: number;
  withoutDocumentCount: number;
  viseIntegralementCount: number;
  viseFavorableCount: number;
  diffusedDocumentCount: number;
  forecastDocumentCount: number;
};
type BuildingSummary = {
  id: number;
  name: string;
  shortName: string | null;
  zones: DoZoneSummary[];
};
type FilterOption = { id: number; name: string };
type SummaryData = {
  filters: {
    lots: FilterOption[];
    performances: Array<FilterOption & { lotId: number }>;
  };
  buildings: BuildingSummary[];
};
type RangOneContract = { id: number; label: string; companyName: string };
type DoIntervention = {
  id: number;
  dateIntervention: string | null;
  lot: { id: number; name: string; nLot: string | null };
  performance: { id: number; name: string };
  rangOneContracts: RangOneContract[];
  documents: DocumentData[];
};
type PageState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; data: SummaryData };
type DetailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; interventions: DoIntervention[] };

const parseIds = (value: string | null) =>
  value
    ?.split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0) ?? [];

export function AvancementEtudesDo() {
  const { yardId } = useParams<{ yardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(
    null,
  );
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [detail, setDetail] = useState<DetailState>({ status: "idle" });
  const lotQuery = searchParams.get("lot") ?? "";
  const performanceQuery = searchParams.get("performance") ?? "";
  const lots = parseIds(lotQuery);
  const performances = parseIds(performanceQuery);
  const recentOnly = searchParams.get("recentOnly") === "true";

  useEffect(() => {
    if (isNil(yardId)) return;
    let cancelled = false;
    // Keep the screen in place while it reloads: unmounting the filters would
    // close the list being ticked.
    setState((current) =>
      current.status === "success" ? current : { status: "loading" },
    );
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/do", {
        params: {
          path: { yardId },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "success", data: data.data });
        setSelectedBuildingId(
          (current) => current ?? data.data.buildings[0]?.id ?? null,
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, lotQuery, performanceQuery]);

  useEffect(() => {
    if (isNil(yardId) || isNil(selectedZoneId)) {
      setDetail({ status: "idle" });
      return;
    }
    let cancelled = false;
    setDetail({ status: "loading" });
    void apiClient
      .GET("/api/yards/{yardId}/study-progress/do/zones/{zoneId}", {
        params: {
          path: { yardId, zoneId: String(selectedZoneId) },
          query: {
            ...(lotQuery ? { lot: lotQuery } : {}),
            ...(performanceQuery ? { performance: performanceQuery } : {}),
            recentOnly: recentOnly ? "true" : "false",
          },
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (isNotNil(error) || isNil(data)) {
          setDetail({ status: "error" });
          return;
        }
        setDetail({
          status: "success",
          interventions: data.data.interventions,
        });
      })
      .catch(() => {
        if (!cancelled) setDetail({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [yardId, selectedZoneId, lotQuery, performanceQuery, recentOnly]);

  const updateFilter = (key: "lot" | "performance", values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(","));
    setSearchParams(next, { replace: true });
  };
  const setRecentOnly = (checked: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set("recentOnly", "true");
    else next.delete("recentOnly");
    setSearchParams(next, { replace: true });
  };

  // A donneur d'ordre changes nothing from this table.
  const { tableProps, overlays } = useDocumentActions({
    yardId,
    lots:
      detail.status === "success"
        ? detail.interventions.flatMap(interventionLots)
        : [],
    onChanged: () => undefined,
  });

  if (state.status === "loading")
    return <PageMessage text="Chargement de l'avancement des études…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger l'avancement des études."
        error
      />
    );
  if (state.data.buildings.length === 0)
    return (
      <PageMessage text="Aucun bâtiment n'est rattaché à votre contrat sur ce chantier." />
    );

  const selectedBuilding =
    state.data.buildings.find((item) => item.id === selectedBuildingId) ??
    state.data.buildings[0];

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden p-4">
      <header className="shrink-0">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          Avancement des études
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          Visualisez l'avancement des études de vos bâtiments par zone.
        </p>
      </header>
      <section
        aria-label="Filtres"
        className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900"
      >
        <div className="w-56">
          <Select
            label="Lots"
            multiple
            options={state.data.filters.lots.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={lots.map(String)}
            onValueChange={({ value }) => updateFilter("lot", value)}
          />
        </div>
        <div className="w-56">
          <Select
            label="Prestations"
            multiple
            options={state.data.filters.performances.map((item) => ({
              value: String(item.id),
              label: item.name,
            }))}
            value={performances.map(String)}
            onValueChange={({ value }) => updateFilter("performance", value)}
          />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={recentOnly}
            onChange={(event) => setRecentOnly(event.target.checked)}
          />
          Indices récents uniquement
        </label>
      </section>
      <BuildingNav
        buildings={state.data.buildings}
        selectedId={selectedBuilding?.id}
        onSelect={(buildingId) => {
          setSelectedBuildingId(buildingId);
          setSelectedZoneId(null);
        }}
      />
      <main className="min-h-0 min-w-0 flex-1 space-y-3 overflow-y-auto">
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
          {selectedBuilding?.name}
        </h3>
        {selectedBuilding?.zones.length === 0 && (
          <p className="text-sm text-zinc-500">Aucune zone dans ce bâtiment.</p>
        )}
        {selectedBuilding?.zones.map((zoneItem) => (
          <Fragment key={zoneItem.id}>
            <ZoneCard
              name={zoneItem.name}
              counters={[
                `${zoneItem.interventionCount} intervention(s)`,
                `${zoneItem.withoutDocumentCount} sans document`,
                `${zoneItem.diffusedDocumentCount} document(s) diffusé(s)`,
                `${zoneItem.forecastDocumentCount} document(s) prévisionnel(s)`,
                `${zoneItem.viseIntegralementCount} visé(s) intégralement (dernier indice reçu)`,
                `${zoneItem.viseFavorableCount} visé(s) intégralement favorablement`,
              ]}
              open={zoneItem.id === selectedZoneId}
              onToggle={() =>
                setSelectedZoneId(
                  zoneItem.id === selectedZoneId ? null : zoneItem.id,
                )
              }
            />
            {zoneItem.id === selectedZoneId && (
              <DoZoneDetail state={detail} tableProps={tableProps} />
            )}
          </Fragment>
        ))}
      </main>
      {overlays}
    </div>
  );
}

function PageMessage({
  text,
  error = false,
}: {
  text: string;
  error?: boolean;
}) {
  return (
    <div
      className={cn(
        "p-8 text-center text-sm",
        error ? "text-red-600" : "text-zinc-500",
      )}
    >
      {text}
    </div>
  );
}

type DoZoneDetailProps = {
  state: DetailState;
  tableProps: Omit<DocumentTableProps, "lots">;
};
function DoZoneDetail({ state, tableProps }: DoZoneDetailProps) {
  if (state.status === "idle" || state.status === "loading")
    return <PageMessage text="Chargement des interventions…" />;
  if (state.status === "error")
    return (
      <PageMessage
        text="Impossible de charger les interventions de cette zone."
        error
      />
    );
  if (state.interventions.length === 0)
    return (
      <PageMessage text="Aucune intervention ne correspond aux filtres." />
    );
  return (
    <div className="space-y-4">
      {state.interventions.map((item) => (
        <article key={item.id}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="font-semibold text-zinc-900 dark:text-zinc-100">
                {item.lot.nLot ? `Lot ${item.lot.nLot} — ` : ""}
                {item.performance.name}
              </h4>
              <p className="mt-1 text-sm text-zinc-500">
                {item.rangOneContracts.length === 0
                  ? "Aucun contrat travaux rang 1 actif"
                  : item.rangOneContracts
                      .map(
                        (contract) =>
                          `${contract.label} (${contract.companyName})`,
                      )
                      .join(" · ")}
              </p>
              {item.rangOneContracts.length > 1 && (
                <p className="mt-1 text-xs text-amber-700">
                  Plusieurs contrats travaux rang 1 actifs
                </p>
              )}
            </div>
            <p className="text-xs font-medium text-zinc-600 dark:text-zinc-300">
              Date de démarrage presta
              <span className="mt-1 block text-sm font-normal">
                {isNotNil(item.dateIntervention)
                  ? formatDate(item.dateIntervention)
                  : "—"}
              </span>
            </p>
          </div>
          <div className="mt-2">
            {item.documents.length === 0 ? (
              <p className="rounded-lg bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500 dark:bg-zinc-800/50">
                Aucun document produit pour cette intervention.
              </p>
            ) : (
              <DocumentTable
                lots={interventionLots(item)}
                inScrollingList
                {...tableProps}
              />
            )}
          </div>
        </article>
      ))}
    </div>
  );
}
